// Overview view model (NAV-002, the signed-in landing): the greeting, this month's figures for a
// principal with `billing:read` (billed, gross profit, active customers, tokens), the 12-month
// "Billed" chart and its table, the top customers, and "Needs your attention" — built from the
// signals the old Home tiles computed: open months past due, CUR freshness, installer sessions
// awaiting approval, recent audit events. Each signal is built only from data the caller could
// fetch (its permission); a signal for data nobody fetched is simply absent (`homeTiles` skips a
// null source). Pure; tests/unit/home.test.ts.
//
// CUR freshness: there is no fleet-wide CUR source read (API-035 is per customer), so home.tsx
// composes it from each known customer's `GET /v1/admin/orgs/{orgId}/cur-sources`.
import type { components } from "../../api/schema";
import type { ChartBar } from "@nexus/ui";
import { formatUsd, microToDollars, ratioBasisPoints, sumMicro, toMicro } from "@nexus/ui";
import { addMonths, currentMonth, longMonthLabel, monthLabel, monthParts, monthsBetween, shortMonthLabel } from "@nexus/ui";
import { basisFigures, notBilledAllcodeCost, type RevenueBreakdown, type RevenueBreakdownItem, type RevenueForecast } from "../revenue/model";

export type BillingPeriod = components["schemas"]["BillingPeriod"];
export type InstallerSession = components["schemas"]["InstallerSession"];
export type AuditEvent = components["schemas"]["AuditEvent"];
export type CurSource = components["schemas"]["CurSource"];

export const HOME_PATH = "/";

export type TileStatus = "ok" | "attention" | "unknown";

export interface Tile {
  key: "open-periods" | "cur-freshness" | "installer-approvals" | "recent-audit";
  title: string;
  href: string;
  status: TileStatus;
  summary: string;
  count: number;
}

function plural(count: number, noun: string, many = `${noun}s`): string {
  return `${count.toLocaleString("en-US")} ${count === 1 ? noun : many}`;
}

/** Open periods past due (`listAllBillingPeriods`, API-020, super-admin only): still open, month ended. */
export function openPeriodsPastDueTile(periods: readonly BillingPeriod[], now: Date): Tile {
  const month = currentMonth(now);
  const pastDue = periods.filter((period) => period.status === "open" && period.period < month);
  return {
    key: "open-periods",
    title: "Open months past due",
    href: "/invoices",
    status: pastDue.length > 0 ? "attention" : "ok",
    summary: pastDue.length === 0 ? "No open months are past due." : `${plural(pastDue.length, "customer-month")} past due.`,
    count: pastDue.length,
  };
}

export const CUR_STALE_AFTER_HOURS = 30; // AWS delivers each CUR export within 24 h of month end (FLOW-007); a margin above that.

function hoursSince(iso: string, now: Date): number {
  return (now.getTime() - Date.parse(iso)) / 3_600_000;
}

/** A CUR source that needs a look: failing, never read, or not read for over `CUR_STALE_AFTER_HOURS`. */
export function isStaleCurSource(source: CurSource, now: Date): boolean {
  if (source.status === "disabled") return false;
  return source.status === "failing" || source.lastSuccessAt === null || hoursSince(source.lastSuccessAt, now) > CUR_STALE_AFTER_HOURS;
}

/** CUR freshness across every linked source (disabled ones are history, not stale). */
export function curFreshnessTile(sources: readonly CurSource[], now: Date): Tile {
  const live = sources.filter((source) => source.status !== "disabled");
  if (live.length === 0) {
    return { key: "cur-freshness", title: "CUR freshness", href: "/aws-costs", status: "unknown", summary: "No CUR sources linked yet.", count: 0 };
  }
  const stale = live.filter((source) => isStaleCurSource(source, now));
  return {
    key: "cur-freshness",
    title: "CUR freshness",
    href: "/aws-costs",
    status: stale.length > 0 ? "attention" : "ok",
    summary: stale.length === 0 ? "Every CUR source is fresh." : `${plural(stale.length, "CUR source")} ${stale.length === 1 ? "needs" : "need"} attention.`,
    count: stale.length,
  };
}

/** Installer sessions awaiting approval (`listInstallerSessions?state=awaiting_approval`, API-040). */
export function installerApprovalsTile(sessions: readonly InstallerSession[]): Tile {
  const awaiting = sessions.filter((session) => session.state === "awaiting_approval");
  return {
    key: "installer-approvals",
    title: "Installer sessions awaiting approval",
    href: "/installer",
    status: awaiting.length > 0 ? "attention" : "ok",
    summary: awaiting.length === 0 ? "No sessions await approval." : `${plural(awaiting.length, "session")} awaiting approval.`,
    count: awaiting.length,
  };
}

/** Recent audit events (`listAuditEvents`, API-037), newest first. */
export function recentAuditTile(events: readonly AuditEvent[]): Tile {
  const latest = events[0];
  return {
    key: "recent-audit",
    title: "Recent audit events",
    href: "/audit",
    status: "ok",
    summary: latest ? `${plural(events.length, "event")} — latest ${latest.action}.` : "No recent audit events.",
    count: events.length,
  };
}

export interface HomeData {
  now: Date;
  /** null when the principal's permissions meant this was never fetched. */
  openPeriods: readonly BillingPeriod[] | null;
  curSources: readonly CurSource[] | null;
  installerSessions: readonly InstallerSession[] | null;
  recentAudit: readonly AuditEvent[] | null;
}

/** The signals to show: one per data source the caller actually fetched, in NAV-002's order. */
export function homeTiles(data: HomeData): Tile[] {
  const tiles: Tile[] = [];
  if (data.openPeriods !== null) tiles.push(openPeriodsPastDueTile(data.openPeriods, data.now));
  if (data.curSources !== null) tiles.push(curFreshnessTile(data.curSources, data.now));
  if (data.installerSessions !== null) tiles.push(installerApprovalsTile(data.installerSessions));
  if (data.recentAudit !== null) tiles.push(recentAuditTile(data.recentAudit));
  return tiles;
}

// ---- "Needs your attention" ----

export type AttentionTone = "green" | "amber" | "grey" | "red" | "blue";

export interface AttentionItem {
  key: Tile["key"];
  title: string;
  detail: string;
  href: string;
  chip: { tone: AttentionTone; label: string };
  status: TileStatus;
}

const STATUS_ORDER: Record<TileStatus, number> = { attention: 0, unknown: 1, ok: 2 };

/** The attention list, most urgent first: each fetched signal as a link with a status chip. */
export function attentionItems(data: HomeData): AttentionItem[] {
  const items: AttentionItem[] = [];
  const previous = addMonths(currentMonth(data.now), -1);
  for (const tile of homeTiles(data)) {
    switch (tile.key) {
      case "open-periods": {
        const months = [...new Set((data.openPeriods ?? []).filter((p) => p.status === "open" && p.period < currentMonth(data.now)).map((p) => p.period))].sort();
        const single = months.length === 1 ? longMonthLabel(months[0] as string) : null;
        items.push(
          tile.status === "attention"
            ? {
                key: tile.key,
                title: single ? `Close ${single} for ${plural(tile.count, "customer")}` : `Close ${plural(tile.count, "open customer-month")}`,
                detail: "Invoices stay drafts until the month is closed.",
                href: tile.href,
                chip: { tone: "amber", label: "Due" },
                status: tile.status,
              }
            : {
                key: tile.key,
                title: `${longMonthLabel(previous)} is closed`,
                detail: "No open months are past due.",
                href: tile.href,
                chip: { tone: "green", label: "Done" },
                status: tile.status,
              },
        );
        break;
      }
      case "cur-freshness":
        items.push(
          tile.status === "unknown"
            ? {
                key: tile.key,
                title: "Actual AWS costs are not connected",
                detail: "Profit is measured at AWS list price until a Cost and Usage Report (CUR) is linked.",
                href: tile.href,
                chip: { tone: "grey", label: "Setup" },
                status: tile.status,
              }
            : tile.status === "attention"
              ? {
                  key: tile.key,
                  title: `${plural(tile.count, "CUR source")} ${tile.count === 1 ? "needs" : "need"} a look`,
                  detail: `Failing, never read, or not read for over ${CUR_STALE_AFTER_HOURS} hours.`,
                  href: tile.href,
                  chip: { tone: "red", label: "Stale" },
                  status: tile.status,
                }
              : {
                  key: tile.key,
                  title: "Every CUR source is fresh",
                  detail: "The actual AWS bill is being read on schedule.",
                  href: tile.href,
                  chip: { tone: "green", label: "Fresh" },
                  status: tile.status,
                },
        );
        break;
      case "installer-approvals":
        items.push({
          key: tile.key,
          title: tile.count > 0 ? `${plural(tile.count, "installer session")} ${tile.count === 1 ? "awaits" : "await"} approval` : "No installer sessions await approval",
          detail: tile.count > 0 ? "A planned change is waiting for an approver." : "Nothing is waiting on you in the installer.",
          href: tile.href,
          chip: tile.count > 0 ? { tone: "amber", label: "Needs approval" } : { tone: "green", label: "Clear" },
          status: tile.status,
        });
        break;
      case "recent-audit": {
        const latest = data.recentAudit?.[0];
        items.push({
          key: tile.key,
          title: latest ? `${plural(tile.count, "recent audit event")}` : "No recent audit events",
          detail: latest ? `Latest: ${latest.action}, ${new Date(latest.occurredAt).toLocaleString()}.` : "Changes people make are recorded in the audit log.",
          href: tile.href,
          chip: { tone: "grey", label: "Audit log" },
          status: tile.status,
        });
        break;
      }
    }
  }
  return items.sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status]);
}

// ---- greeting and this month ----

/** "Chad" from "Chad Grant"; null when the name is unknown (never an email or a subject). */
export function firstName(name: string | null | undefined): string | null {
  const first = name?.trim().split(/\s+/)[0];
  return first ? first : null;
}

/** "Good afternoon, Chad" by the local hour, or "Overview" without a name. */
export function greeting(name: string | null | undefined, now: Date): string {
  const first = firstName(name);
  if (!first) return "Overview";
  const hour = now.getHours();
  const part = hour < 12 ? "morning" : hour < 18 ? "afternoon" : "evening";
  return `Good ${part}, ${first}`;
}

export interface MonthProgress {
  month: string;
  day: number;
  daysInMonth: number;
}

/** How far through the current UTC month `now` is. */
export function monthProgress(now: Date): MonthProgress {
  const month = currentMonth(now);
  const { year, index } = monthParts(month);
  const daysInMonth = new Date(Date.UTC(year, index + 1, 0)).getUTCDate();
  return { month, day: now.getUTCDate(), daysInMonth };
}

/** "September 2026 so far, 25 of 30 days. Figures are month to date at AWS list price." */
export function overviewSubtitle(progress: MonthProgress): string {
  return `${longMonthLabel(progress.month)} so far, ${progress.day} of ${progress.daysInMonth} days. Figures are month to date at AWS list price.`;
}

/** 1,840,000,000 → "1.84 B"; 12,300 → "12.3 k"; small counts as they are. */
export function formatCount(value: number): string {
  const units: [number, string][] = [
    [1e12, "T"],
    [1e9, "B"],
    [1e6, "M"],
    [1e3, "k"],
  ];
  for (const [size, unit] of units) {
    if (Math.abs(value) >= size) {
      const scaled = value / size;
      const digits = Math.abs(scaled) >= 100 ? 0 : Math.abs(scaled) >= 10 ? 1 : 2;
      return `${Number(scaled.toFixed(digits))} ${unit}`;
    }
  }
  return value.toLocaleString("en-US");
}

export interface OverviewStats {
  billed: bigint;
  billedNote: string;
  grossProfit: bigint;
  grossProfitNote: string;
  activeCustomers: number;
  knownCustomers: number | null;
  activeNote: string;
  tokens: number;
  tokensNote: string;
}

export interface OverviewInput {
  now: Date;
  /** `by-month` for the last 12 months up to the current one (list basis). */
  byMonth: RevenueBreakdown | null;
  /** `by-org` for the current month (list basis), with its not-billed customers. */
  byOrg: RevenueBreakdown | null;
  forecast: RevenueForecast | null;
  /** How many customers the app knows (the directory), when it has loaded. */
  knownCustomers: number | null;
}

function monthItem(breakdown: RevenueBreakdown | null, period: string): RevenueBreakdownItem | undefined {
  return breakdown?.items.find((item) => item.key === period);
}

/** The four stat cards for this month, at AWS list price. */
export function overviewStats(input: OverviewInput): OverviewStats {
  const { month } = monthProgress(input.now);
  const previous = addMonths(month, -1);
  const current = monthItem(input.byMonth, month);
  const figures = current ? basisFigures(current, "list") : { billed: 0n, awsCost: 0n, profit: 0n, provisional: false };
  const notBilled = input.byOrg?.notBilled ?? [];
  const grossProfit = figures.profit - notBilledAllcodeCost(notBilled, "list");
  const margin = ratioBasisPoints(grossProfit, figures.billed);
  const projected = input.forecast && input.forecast.period === month ? sumMicro(input.forecast.items.map((item) => item.projected.priceMicroUsd)) : null;
  const last = monthItem(input.byMonth, previous);
  const billedNote =
    projected !== null && projected > 0n ? `On pace for ${formatUsd(projected)}` : last ? `${longMonthLabel(previous)}: ${formatUsd(last.priceMicroUsd)}` : "";

  const billedActive = (input.byOrg?.items ?? []).filter((item) => item.requests > 0).length;
  const notBilledActive = notBilled.filter((item) => item.requests > 0).length;
  const active = billedActive + notBilledActive;
  const known = input.knownCustomers !== null && input.knownCustomers >= active ? input.knownCustomers : null;

  const billedTokens = input.byOrg ? input.byOrg.items.reduce((sum, item) => sum + item.tokens, 0) : (current?.tokens ?? 0);
  const tokens = billedTokens + notBilled.reduce((sum, item) => sum + item.tokens, 0);
  const requests = (input.byOrg ? input.byOrg.items.reduce((sum, item) => sum + item.requests, 0) : (current?.requests ?? 0)) + notBilled.reduce((sum, item) => sum + item.requests, 0);

  return {
    billed: figures.billed,
    billedNote,
    grossProfit,
    grossProfitNote: margin === null ? "No billed usage yet" : `${formatMarginBp(margin)} margin`,
    activeCustomers: active,
    knownCustomers: known,
    activeNote: `${billedActive} billed · ${notBilledActive} not billed`,
    tokens,
    tokensNote: `${formatCount(requests)} ${requests === 1 ? "request" : "requests"}`,
  };
}

function formatMarginBp(bp: bigint): string {
  const value = Number(bp) / 100;
  return `${value < 0 ? "−" : ""}${Math.abs(value).toFixed(1)}%`;
}

/** One bar per month for the last 12 months (the current month, still running, drawn lighter). */
export function billedBars(byMonth: RevenueBreakdown | null, now: Date): ChartBar[] {
  const month = currentMonth(now);
  return monthsBetween(addMonths(month, -11), month).map((period) => {
    const billed = toMicro(monthItem(byMonth, period)?.priceMicroUsd ?? 0);
    const running = period === month;
    return {
      key: period,
      label: shortMonthLabel(period),
      tooltip: `${monthLabel(period)}${running ? " (so far)" : ""}: billed ${formatUsd(billed)}`,
      segments: [{ series: "billed", value: microToDollars(billed) }],
      muted: running,
    };
  });
}

export interface BilledMonthRow {
  month: string;
  label: string;
  billed: bigint;
  profit: bigint;
  running: boolean;
}

/** The chart's numbers as a table, newest first. */
export function billedRows(byMonth: RevenueBreakdown | null, now: Date): BilledMonthRow[] {
  const month = currentMonth(now);
  return monthsBetween(addMonths(month, -11), month)
    .reverse()
    .map((period) => {
      const item = monthItem(byMonth, period);
      return {
        month: period,
        label: monthLabel(period),
        billed: toMicro(item?.priceMicroUsd ?? 0),
        profit: toMicro(item?.marginMicroUsd ?? 0),
        running: period === month,
      };
    });
}

export interface TopCustomer {
  id: string;
  name: string;
  billed: bigint;
  /** Share of this month's total billed, 0..1. */
  share: number;
}

/** The biggest bills this month, largest first. */
export function topCustomers(byOrg: RevenueBreakdown | null, limit = 5): TopCustomer[] {
  const items = byOrg?.items ?? [];
  const total = sumMicro(items.map((item) => (item.priceMicroUsd > 0 ? item.priceMicroUsd : 0)));
  return [...items]
    .sort((a, b) => b.priceMicroUsd - a.priceMicroUsd || a.label.localeCompare(b.label))
    .slice(0, limit)
    .map((item) => ({
      id: item.key,
      name: item.label || item.key,
      billed: toMicro(item.priceMicroUsd),
      share: total > 0n && item.priceMicroUsd > 0 ? Number((toMicro(item.priceMicroUsd) * 10_000n) / total) / 10_000 : 0,
    }));
}

/** What the landing shows: the finance overview, the operations signals only, or an orientation. */
export type OverviewKind = "finance" | "signals" | "orientation";

export function overviewKind(access: { billing: boolean; installer: boolean }, signals: number): OverviewKind {
  if (access.billing) return "finance";
  if (access.installer || signals > 0) return "signals";
  return "orientation";
}
